import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Store, ShoppingBag, MapPin, ArrowRight } from "lucide-react";

import { get } from "@/lib/api-client";
import { formatCurrency } from "@/lib/format";
import { Skeleton } from "@/components/ui/Skeleton";
import type { Product } from "@/types/catalog";
import type { Agent } from "@/types/agent";

export function VendorShopPage() {
  const { vendorId } = useParams();

  // 1. Fetch Vendor Details
  const { data: vendors, isLoading: vendorLoading } = useQuery({
    queryKey: ["public-vendors"],
    queryFn: () => get<Agent[]>("/public/vendors"),
  });

  const vendor = vendors?.find((v) => v.id === Number(vendorId));
  const displayName = vendor?.shop_name || (vendor ? `${vendor.full_name}'s Shop` : "Loading...");

  // 2. Fetch Products for this specific Vendor
  const { data: products, isLoading: productsLoading } = useQuery({
    queryKey: ["vendor-products", vendorId],
    // Passing vendor_id as a query param to filter products
    queryFn: () => get<Product[]>("/catalog/products", { vendor_id: vendorId }),
    enabled: !!vendorId,
  });

  if (vendorLoading || productsLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 space-y-8">
        <Skeleton className="h-64 w-full rounded-3xl" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-64 rounded-2xl" />)}
        </div>
      </div>
    );
  }

  if (!vendor) {
    return (
      <div className="text-center py-32 text-surface-500 flex flex-col items-center">
        <Store className="w-16 h-16 mb-4 text-surface-300" />
        <h2 className="text-2xl font-bold text-surface-900">Vendor Not Found</h2>
        <p className="mt-2">This shop may have been suspended or removed.</p>
        <Link to="/" className="mt-6 text-brand-600 font-bold hover:underline">Go back home</Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface-50 flex flex-col">
      <main className="flex-1 w-full max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10 space-y-12">
        
        {/* 🏪 VENDOR HERO HEADER */}
        <div className="relative overflow-hidden rounded-[2rem] bg-surface-900 px-6 py-10 lg:px-12 lg:py-16 shadow-xl flex items-center gap-6 md:gap-10">
          <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-brand-500/20 rounded-full blur-[100px] -translate-y-1/2 translate-x-1/3 pointer-events-none" />
          
          <div className="relative z-10 shrink-0">
            {vendor.shop_logo ? (
              <img src={vendor.shop_logo} alt={displayName} className="w-24 h-24 sm:w-32 sm:h-32 rounded-2xl object-cover bg-white shadow-lg border-4 border-white/10" />
            ) : (
              <div className="w-24 h-24 sm:w-32 sm:h-32 rounded-2xl bg-white border border-surface-200 text-surface-500 flex items-center justify-center font-bold text-4xl shadow-lg">
                {displayName.charAt(0)}
              </div>
            )}
          </div>
          
          <div className="relative z-10 text-white">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/10 border border-white/20 text-brand-100 text-[10px] sm:text-xs font-semibold uppercase tracking-widest mb-3 backdrop-blur-md">
              <MapPin className="h-3.5 w-3.5 text-warning-400" /> Verified Agent
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-tight">
              {displayName}
            </h1>
            {vendor.shop_description && (
              <p className="mt-3 text-sm sm:text-base text-surface-300 max-w-2xl leading-relaxed">
                {vendor.shop_description}
              </p>
            )}
          </div>
        </div>

        {/* 🛍️ VENDOR PRODUCTS */}
        <section>
          <div className="flex items-center gap-3 mb-6 sm:mb-8 pl-2 border-l-4 border-brand-500">
            <h2 className="text-2xl font-extrabold text-surface-900 tracking-tight ml-2">All Products</h2>
            <div className="px-2.5 py-0.5 bg-surface-200 text-surface-700 rounded-full text-sm font-bold">
              {products?.length || 0}
            </div>
          </div>
          
          {products && products.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-5">
              {products.map((product: any) => {
                const primary = product.images?.find((i: any) => i.is_primary) ?? product.images?.[0] ?? null;

                // 🛠️ FIXED: Smart Price Logic for Vendor Shop Page
                const price1 = Number(product.base_price) || 0;
                const price2 = product.compare_at_price ? Number(product.compare_at_price) : 0;

                let sellingPrice = price1;
                let crossedOutPrice = null;

                if (price2 > 0) {
                  if (price2 > price1) {
                    crossedOutPrice = price2;
                    sellingPrice = price1;
                  } else {
                    crossedOutPrice = price1;
                    sellingPrice = price2;
                  }
                }

                return (
                  <Link key={product.id} to={`/catalog/${product.product_code}`} className="group relative block bg-white rounded-[1.25rem] border border-surface-200/80 overflow-hidden shadow-sm hover:shadow-elevate-2 transition-all duration-300 hover:-translate-y-1 flex flex-col h-full z-10 hover:z-20">
                    <div className="aspect-[4/5] bg-surface-50 relative overflow-hidden shrink-0">
                      {primary ? (
                        <img src={primary.image} alt={product.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                      ) : (
                        <div className="h-full w-full flex items-center justify-center text-surface-300 bg-surface-100/50"><ShoppingBag className="h-10 w-10 opacity-30" /></div>
                      )}
                      
                      {/* 🛠️ FIXED: Only show SALE badge if there is a crossed out price */}
                      {crossedOutPrice && (
                        <div className="absolute top-2 left-2 flex flex-col gap-1.5 items-start">
                          <div className="bg-danger-600/95 backdrop-blur text-white text-[10px] px-2 py-0.5 rounded-md font-bold shadow-sm">
                            SALE
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="p-3 sm:p-4 flex flex-col flex-1 bg-white relative z-10">
                      <div className="text-[10px] font-bold uppercase tracking-wider text-surface-500 mb-1">{product.category?.name}</div>
                      <h3 className="font-bold text-surface-900 text-xs sm:text-sm line-clamp-2 leading-snug flex-1 group-hover:text-brand-600 transition-colors">{product.name}</h3>
                      
                      <div className="mt-3 sm:mt-4 pt-2 border-t border-surface-100/80 flex items-end justify-between">
                        <div>
                          {/* 🛠️ FIXED: Show correctly calculated prices */}
                          {crossedOutPrice && (
                            <div className="text-[10px] text-surface-400 line-through mb-0.5">
                              {formatCurrency(crossedOutPrice)}
                            </div>
                          )}
                          <div className="text-sm sm:text-base font-extrabold text-brand-700 leading-none tracking-tight">
                            {formatCurrency(sellingPrice)}
                          </div>
                        </div>
                        
                        <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-surface-100 text-surface-600 flex items-center justify-center group-hover:bg-brand-600 group-hover:text-white transition-colors">
                          <ArrowRight className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                        </div>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          ) : (
            <div className="py-16 text-center bg-white border border-surface-200 border-dashed rounded-[2rem] text-surface-500 font-medium flex flex-col items-center justify-center">
              <Store className="w-12 h-12 text-surface-300 mb-4" />
              This vendor hasn't listed any products yet.
            </div>
          )}
        </section>
      </main>
    </div>
  );
}